import React, { useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { HiChevronLeft as ArrowLeft, HiXMark as CrossIcon } from 'react-icons/hi2';
import { Button, Dialog } from '@knack/asterisk-react';

import { StepComponent } from './StepComponent';
import { steps } from './steps';
import { type QuestionnaireData, type RadioSelected, type SelectedData } from './types';
import { transformData } from './utils';

type SkipModalProps = {
  open: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onSkip: () => void;
};

function SkipModal({ open, setIsOpen, onSkip }: SkipModalProps) {
  const [t] = useTranslation();

  return (
    <Dialog open={open} onOpenChange={setIsOpen}>
      <Dialog.Content className="bg-default">
        <Dialog.MainContent>
          <Dialog.Header className="mb-6 text-lg font-medium text-emphasis">
            {t('components.onboarding.questionnaire.skip_modal.title')}
          </Dialog.Header>
          <div className="text-sm">
            {t('components.onboarding.questionnaire.skip_modal.subtitle')}
          </div>
        </Dialog.MainContent>
        <Dialog.Footer className="gap-2 pt-2">
          <Button intent="minimal" onClick={() => setIsOpen(false)}>
            {t('actions.cancel')}
          </Button>
          <Button onClick={onSkip}>{t('components.onboarding.questionnaire.skip')}</Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}

export function Questionnaire({
  onSubmit,
  loading
}: {
  onSubmit: (data: QuestionnaireData) => void;
  loading: boolean;
}) {
  const [t] = useTranslation();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selectedData, setSelectedData] = useState<SelectedData>({});
  const [shouldShowSkipModal, setShouldShowSkipModal] = useState(false);

  const onContinue = () => {
    if (steps.length === currentStepIndex + 1) {
      onSubmit(transformData(selectedData));
      return;
    }

    setCurrentStepIndex((step) => step + 1);
  };

  const onBack = () => {
    if (currentStepIndex === 0) {
      return;
    }

    setCurrentStepIndex((step) => step - 1);
  };

  const onSkip = () => {
    onSubmit(transformData(selectedData));
  };

  const onStepChangeSelection = (data: RadioSelected) => {
    setSelectedData((old) => ({
      ...old,
      [steps[currentStepIndex].id]: data
    }));
  };

  const currentStep = steps[currentStepIndex];
  const stepSelectedData = selectedData[currentStep.id];
  const previousSelection = selectedData[steps[currentStepIndex - 1]?.id]?.option;
  const otherSelected = stepSelectedData?.option === 'other';
  const stepOptions = currentStep.data.conditional
    ? currentStep.data.options[previousSelection!]
    : currentStep.data.options;

  return (
    <section className="mb-12 flex-auto md:flex">
      <button
        aria-label={t('components.onboarding.questionnaire.skip_label')}
        type="button"
        className="absolute right-4 top-4 z-10 md:right-4 md:top-10 xl:right-15"
        onClick={() => setShouldShowSkipModal(true)}
      >
        <CrossIcon className="h-8 w-8" />
      </button>
      <div className="container mt-20 max-w-[800px] md:mt-0">
        <div className="mx-auto w-full sm:text-center md:text-left">
          <h1
            className="mb-2 text-3xl font-medium text-emphasis"
            data-testid="onboarding-questionnaire-title"
          >
            {t('components.onboarding.questionnaire.title')}
          </h1>
          <p className="mb-7 text-sm text-subtle">
            {t('components.onboarding.questionnaire.description')}
          </p>
          <div className="gap mb-12 flex flex-row justify-center gap-2 md:justify-start">
            {steps.map(({ id }, i) => (
              <div
                key={id}
                data-testid={`onboarding-questionnaire-step-indicator-${i + 1}`}
                data-active={currentStepIndex >= i}
                className={`h-2 w-16 rounded ${
                  i > currentStepIndex ? 'bg-subtle' : 'bg-gradient-2'
                }`}
              />
            ))}
          </div>

          <StepComponent
            question={currentStep}
            disabled={loading}
            selected={stepSelectedData}
            options={stepOptions}
            onChangeSelection={onStepChangeSelection}
          />

          <div className="float-right mt-7 flex flex-row gap-2">
            <Button intent="minimal" onClick={onSkip} disabled={loading}>
              {t('components.onboarding.questionnaire.skip')}
            </Button>
            {currentStepIndex > 0 && (
              <Button intent="secondary" onClick={onBack} disabled={loading}>
                <Button.Icon position="left" icon={ArrowLeft} />
                {t('components.onboarding.questionnaire.back')}
              </Button>
            )}
            <Button
              onClick={onContinue}
              disabled={
                stepSelectedData?.option === undefined ||
                (!stepOptions.includes(stepSelectedData.option) && !otherSelected) ||
                loading ||
                (otherSelected && !stepSelectedData?.other?.trim())
              }
              data-testid="onboarding-questionnaire-continue"
            >
              {t('components.onboarding.questionnaire.continue')}
            </Button>
          </div>
        </div>
      </div>

      <SkipModal open={shouldShowSkipModal} setIsOpen={setShouldShowSkipModal} onSkip={onSkip} />
    </section>
  );
}
